// Formatting + wire-value helpers local to the landed-cost feature (G-WebB).
// Duplicated from the equivalent helpers in features/purchasing-intake/common.ts
// and features/sales-invoicing/format.ts rather than imported cross-feature —
// same precedent those files' own headers document (each feature owns its
// presentation-only formatters; purchasing-intake/api.ts is explicitly off-limits
// to this goal, and every prior web goal duplicated rather than reached across a
// feature boundary for this exact reason).

/** ZAR cents (bigint, its wire decimal-string form, or a plain number) -> "R1 234,56"
 * (en-ZA: space-thousands, comma-decimal — SA convention, not US). Grouping goes
 * through `Intl.NumberFormat('en-ZA').format(rand)` on the bigint whole-rand
 * value directly (its `.format` overload accepts `bigint`, no `Number(cents)`
 * conversion), so precision is exact even for amounts beyond
 * Number.MAX_SAFE_INTEGER; the cents remainder is likewise derived from the
 * bigint's own `% 100n`, never floating point. */
export function formatMoneyCents(cents: bigint | string | number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  const value = typeof cents === 'bigint' ? cents : BigInt(Math.trunc(Number(cents)));
  const negative = value < 0n;
  const abs = negative ? -value : value;
  const rand = abs / 100n;
  const remainder = (abs % 100n).toString().padStart(2, '0');
  const grouped = new Intl.NumberFormat('en-ZA').format(rand);
  return `${negative ? '-' : ''}R${grouped},${remainder}`;
}

/** User-typed Rand amount (e.g. "1234.56") -> integer ZAR cents as a decimal
 * string, for wire bodies that use `moneyCentsSchema` (bigint-or-numeric-string).
 * Deterministic string/BigInt math (never `Math.round(x * 100)`) so entering an
 * amount never drifts off a cent to floating-point error. */
export function randToCents(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '0';
  const negative = trimmed.startsWith('-');
  const abs = negative ? trimmed.slice(1) : trimmed;
  const [wholePart, fracPart = ''] = abs.split('.');
  const frac = `${fracPart}00`.slice(0, 2);
  const digits = `${wholePart || '0'}${frac}`.replace(/^0+(?=\d)/, '');
  const cents = BigInt(digits || '0');
  return (negative ? -cents : cents).toString();
}

/** Inverse of `randToCents` — integer ZAR cents (wire decimal string) -> a plain
 * Rand string for a form input, e.g. "1234.56". */
export function centsToRand(cents: string | null | undefined): string {
  if (cents == null || cents === '') return '';
  const value = BigInt(cents);
  const negative = value < 0n;
  const abs = negative ? -value : value;
  const rand = abs / 100n;
  const remainder = (abs % 100n).toString().padStart(2, '0');
  return `${negative ? '-' : ''}${rand.toString()}.${remainder}`;
}
